if (!customElements.get('savalion-roast-switcher')) {
  customElements.define(
    'savalion-roast-switcher',
    class SavalionRoastSwitcher extends HTMLElement {
      #abortController = null;
      #onClick = this.#handleClick.bind(this);

      connectedCallback() {
        this.addEventListener('click', this.#onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.#abortController?.abort();
      }

      async #handleClick(event) {
        const link = event.target.closest('a[data-roast-url]');
        if (!link || this.classList.contains('is-loading')) return;

        event.preventDefault();

        const productInfo = this.closest('product-info');
        if (!productInfo) {
          window.location.assign(link.href);
          return;
        }

        const sectionId = this.dataset.sectionId || productInfo.dataset.section;
        const requestUrl = new URL(link.href, window.location.origin);
        requestUrl.searchParams.set('section_id', sectionId);

        this.#abortController?.abort();
        this.#abortController = new AbortController();
        this.classList.add('is-loading');
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(requestUrl.toString(), {
            signal: this.#abortController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextProductInfo = documentFragment.querySelector('product-info');
          const nextPurchase = documentFragment.querySelector('.savalion-product-hero__purchase');
          const currentPurchase = productInfo.querySelector('.savalion-product-hero__purchase');

          if (!nextProductInfo || !nextPurchase || !currentPurchase) throw new Error('Missing purchase area');

          currentPurchase.replaceWith(nextPurchase);

          if (nextProductInfo.dataset.url) productInfo.dataset.url = nextProductInfo.dataset.url;
          if (nextProductInfo.dataset.productId) productInfo.dataset.productId = nextProductInfo.dataset.productId;

          const nextTitle = documentFragment.querySelector('[data-page-title]')?.textContent?.trim();
          if (nextTitle) document.title = nextTitle;
          window.history.replaceState({}, '', link.href);

          productInfo.cartUpdateUnsubscriber?.();
          productInfo.quantityInput = productInfo.querySelector('.quantity__input');
          productInfo.quantityForm = null;
          productInfo.initQuantityHandlers?.();
          window?.Shopify?.PaymentButton?.init();
          productInfo.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('savalion:purchase-swapped', { detail: { productInfo } }));
          nextPurchase.querySelector('input, button, select, a[href]')?.focus();
        } catch (error) {
          if (error.name !== 'AbortError') {
            window.location.assign(link.href);
          }
        } finally {
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
        }
      }
    }
  );
}

if (!customElements.get('savalion-sticky-atc')) {
  customElements.define(
    'savalion-sticky-atc',
    class SavalionStickyAtc extends HTMLElement {
      #observer = null;
      #mutationObserver = null;
      #productInfo = null;
      #purchaseTarget = null;
      #onSwap = this.#handleSwap.bind(this);

      connectedCallback() {
        this.#productInfo = document.getElementById(this.dataset.productInfoId);
        if (!this.#productInfo) return;

        this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
          this.#productInfo.querySelector('[name="add"]')?.click();
        });

        this.querySelector('[data-sticky-buy]')?.addEventListener('click', () => {
          this.#productInfo.querySelector('.shopify-payment-button__button')?.click();
        });

        document.addEventListener('savalion:purchase-swapped', this.#onSwap);
        this.#connectObserver();
        this.#syncMeta();
        this.#syncPrice();
        this.#syncBuyNowState();
      }

      disconnectedCallback() {
        document.removeEventListener('savalion:purchase-swapped', this.#onSwap);
        this.#observer?.disconnect();
        this.#mutationObserver?.disconnect();
      }

      #handleSwap(event) {
        if (event.detail?.productInfo !== this.#productInfo) return;
        this.#connectObserver();
        this.#syncMeta();
        this.#syncPrice();
        this.#syncBuyNowState();
      }

      #connectObserver() {
        this.#observer?.disconnect();
        this.#mutationObserver?.disconnect();
        this.#purchaseTarget = this.#productInfo.querySelector('.savalion-product-hero__purchase');
        if (!this.#purchaseTarget) return;

        this.#observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { threshold: 0.15 }
        );
        this.#observer.observe(this.#purchaseTarget);

        this.#mutationObserver = new MutationObserver(() => {
          this.#syncMeta();
          this.#syncPrice();
          this.#syncBuyNowState();
        });
        this.#mutationObserver.observe(this.#purchaseTarget, { childList: true, subtree: true, characterData: true });
      }

      #syncMeta() {
        const titleOutput = this.querySelector('[data-sticky-title]');
        const variantOutput = this.querySelector('[data-sticky-variant]');
        const title = this.#purchaseTarget?.querySelector('.product__title h1')?.textContent?.replace(/\s+/g, ' ').trim();
        if (titleOutput && title) titleOutput.textContent = title;

        if (variantOutput) {
          const selectedVariantJson = this.#purchaseTarget?.querySelector('variant-selects [data-selected-variant]')?.textContent;
          if (!selectedVariantJson) {
            variantOutput.hidden = true;
            return;
          }

          try {
            const variant = JSON.parse(selectedVariantJson);
            variantOutput.textContent = variant?.title || '';
            variantOutput.hidden = !variant?.title || variant.title === 'Default Title';
          } catch (error) {
            variantOutput.hidden = true;
          }
        }
      }

      #syncPrice() {
        const priceRoot = this.#purchaseTarget?.querySelector('[data-price-source]');
        if (!priceRoot) return;

        const candidates = [
          priceRoot.querySelector('.price-item--sale.price-item--last'),
          priceRoot.querySelector('.price__sale .price-item--sale'),
          priceRoot.querySelector('.price__regular .price-item--regular'),
          priceRoot.querySelector('.price-item--regular'),
        ].filter(Boolean);

        const nextText = candidates
          .map((node) => node.textContent.replace(/\s+/g, ' ').trim())
          .find(Boolean);

        if (nextText) {
          const output = this.querySelector('[data-sticky-price]');
          if (output) output.textContent = nextText;
        }
      }

      #syncBuyNowState() {
        const stickyBuyButton = this.querySelector('[data-sticky-buy]');
        if (!stickyBuyButton) return;
        stickyBuyButton.hidden = !this.#purchaseTarget?.querySelector('.shopify-payment-button__button');
      }
    }
  );
}

if (!customElements.get('savalion-scroll-carousel')) {
  customElements.define(
    'savalion-scroll-carousel',
    class SavalionScrollCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        if (!this.track || !this.dots.length) return;

        this.track.addEventListener('scroll', () => this.#updateDots(), { passive: true });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = Number(dot.dataset.carouselDot);
            const item = this.track.querySelectorAll('[data-carousel-item]')[index];
            item?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
        });

        this.#updateDots();
      }

      #updateDots() {
        const items = Array.from(this.track.querySelectorAll('[data-carousel-item]'));
        if (!items.length) return;

        const currentScroll = this.track.scrollLeft + this.track.clientWidth / 2;
        let activeIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.clientWidth / 2;
          const distance = Math.abs(itemCenter - currentScroll);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      }
    }
  );
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-best-seller-add]');
  if (!button || button.hasAttribute('disabled')) return;

  event.preventDefault();
  button.setAttribute('disabled', 'disabled');
  const originalText = button.textContent;
  button.textContent = '…';

  try {
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const res = await response.json();
    button.textContent = '✓';

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(res);
    }
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'best-seller',
        productVariantId: Number(button.dataset.variantId),
        cartData: res,
      });
    }
  } catch (error) {
    button.textContent = originalText;
  } finally {
    window.setTimeout(() => {
      button.removeAttribute('disabled');
      button.textContent = originalText;
    }, 1200);
  }
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('.accordion-btn');
  if (!button) return;

  const content = button.nextElementSibling;
  if (!content) return;

  const isOpen = button.classList.contains('open');
  button.classList.toggle('open');
  button.setAttribute('aria-expanded', String(!isOpen));
  content.style.maxHeight = isOpen ? '0' : `${content.scrollHeight}px`;
});

document.addEventListener('savalion:purchase-swapped', (event) => {
  event.detail?.productInfo?.querySelectorAll('.accordion-btn').forEach((button) => {
    button.classList.remove('open');
    button.setAttribute('aria-expanded', 'false');
    const content = button.nextElementSibling;
    if (content) content.style.maxHeight = '0';
  });
});
